// src/stores/cartStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem } from '@/types/cart';
import { trackCartAdd, trackCartRemove } from '../services/tracking';

interface CartState {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      addToCart: (item) => {
        const { items } = get();
        const existing = items.find((i) => i.id === item.id);
        if (existing) {
          set({
            items: items.map((i) =>
              i.id === item.id
                ? { ...i, quantite: i.quantite + item.quantite, selectedVariants: item.selectedVariants ?? i.selectedVariants }
                : i
            ),
          });
        } else {
          set({ items: [...items, item] });
        }
        trackCartAdd(item.id, item.nom, item.quantite, item.prix);
      },

      removeFromCart: (id) => {
        const item = get().items.find((i) => i.id === id);
        set((state) => ({ items: state.items.filter((i) => i.id !== id) }));
        if (item) trackCartRemove(item.id, item.nom);
      },

      updateQuantity: (id, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => i.id !== id)
              : state.items.map((i) =>
                  i.id === id ? { ...i, quantite: quantity } : i
                ),
        })),

      clearCart: () => set({ items: [] }),

      isInCart: (id) => get().items.some((i) => i.id === id),
    }),
    { name: 'ornoir-cart' }
  )
);

export const useCartCount = () =>
  useCartStore((s) => s.items.reduce((sum, i) => sum + i.quantite, 0));

export const useCartTotal = () =>
  useCartStore((s) => s.items.reduce((sum, i) => sum + i.prix * i.quantite, 0));

export const useIsInCart = (id: string) =>
  useCartStore((s) => s.items.some((i) => i.id === id));
